import dateTimezone from '@/utils/date-timezone';
import { addHours, format, isBefore, subDays } from 'date-fns';
import { ClientBrandConfig } from 'types/brand-config';

export type CancellationWindowInput = {
  gracePeriod: number;
  nightsBeforeCheckIn: number;
  arriveDate: string;
  timezone: string;
};

const getNonCancelableDate = ({
  nightsBeforeCheckIn,
  arriveDate,
  timezone,
}: Pick<
  CancellationWindowInput,
  'nightsBeforeCheckIn' | 'arriveDate' | 'timezone'
>) => subDays(dateTimezone(arriveDate, timezone), nightsBeforeCheckIn);

/**
 * Long-fuse stay: check-in is far enough that the listing still has a
 * refundable-until date. Short-fuse stays are already inside that window,
 * so BNPL's grace-then-non-refundable policy does not change the terms.
 */
export const isLongFuseCancellationWindow = ({
  gracePeriod,
  nightsBeforeCheckIn,
  arriveDate,
  timezone,
}: CancellationWindowInput): boolean => {
  const checkInDateTZ = dateTimezone(arriveDate, timezone);
  const nowTZ = dateTimezone(new Date().toISOString(), timezone);
  const nonCancelableDate = getNonCancelableDate({
    nightsBeforeCheckIn,
    arriveDate,
    timezone,
  });
  const gracePeriodDate = addHours(nowTZ, gracePeriod);
  return (
    !isBefore(checkInDateTZ, nonCancelableDate) &&
    !isBefore(nonCancelableDate, gracePeriodDate)
  );
};

const CancellationBanner = ({
  gracePeriod,
  nightsBeforeCheckIn,
  arriveDate,
  brand,
}: {
  gracePeriod: number; // Hours
  nightsBeforeCheckIn: number; // Nights
  arriveDate: string; // YYYY-MM-DD
  brand: ClientBrandConfig;
}) => {
  const nonCancelableDate = getNonCancelableDate({
    nightsBeforeCheckIn,
    arriveDate,
    timezone: brand.timezone,
  });
  const cancelText = isLongFuseCancellationWindow({
    gracePeriod,
    nightsBeforeCheckIn,
    arriveDate,
    timezone: brand.timezone,
  })
    ? `Fully refundable until ${format(nonCancelableDate, 'MMM d')}`
    : `Free cancellation for ${gracePeriod} hours`;

  return (
    <div className="group relative flex w-full items-center gap-2.5 rounded-md border border-success-lighter bg-success-light/10 p-4 text-success-darker lg:p-2.5">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-5 w-5 lg:h-6 lg:w-6"
        viewBox="0 0 24 24"
      >
        <path
          fill="currentColor"
          d="M9 2a1 1 0 0 1 1 1v1h4V3a1 1 0 1 1 2 0v1h3a2 2 0 0 1 2 2v13a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h3V3a1 1 0 0 1 1-1M8 6H5v3h14V6h-3v1a1 1 0 1 1-2 0V6h-4v1a1 1 0 0 1-2 0zm11 5H5v8h14z"
        />
      </svg>
      <span className="text-sm font-semibold text-success-darker lg:text-base">
        {cancelText}
      </span>
    </div>
  );
};

export default CancellationBanner;
